import { Check, LayersThree01 } from '@untitled-ui/icons-react/build/cjs';
import React, { useState } from 'react';
import { useTranslation } from 'react-i18next';
import ButtonCmp from 'src/components/ButtonCmp';
import OutsideClickHandler from 'src/components/OutsideClickHandler';
import TooltipCmp from 'src/components/TooltipCmp';

import { TRACK_MAP_THEMES } from './track.const';
import { useTrackMap } from './TrackMapContext';

interface IProps {
    // Matches the surrounding header's button styling: 'btn-outline-primary' on the
    // list page, 'btn_secondary_black' on the detail page.
    buttonClassName?: string;
}

/**
 * Base-style picker for the shared Radar map (Light / Streets / Satellite / Outdoors).
 *
 * Only the open/closed state is local — the applied style and the setter both come
 * from TrackMapContext, so switching from either page drives the same map and both
 * pages re-add their own layers off the resulting styleEpoch bump.
 */
const TrackMapThemePicker: React.FC<IProps> = ({ buttonClassName = 'btn-outline-primary' }) => {
    const { t } = useTranslation();
    const { mapStyle, setMapStyle } = useTrackMap();
    const [isOpen, setIsOpen] = useState(false);

    return (
        <OutsideClickHandler onOutsideClick={() => setIsOpen(false)} containerClassName="relative flex">
            <TooltipCmp message={t('Radar.Map_Theme')} childrenClassName="flex">
                <ButtonCmp size="square-sm" className={buttonClassName} icon={<LayersThree01 className="w-4 h-4" />} onClick={() => setIsOpen((prev) => !prev)}>
                    <></>
                </ButtonCmp>
            </TooltipCmp>

            {isOpen && (
                <ul className="absolute top-full right-0 mt-1.5 z-[3] min-w-[160px] bg-white border border-utilityGray200 rounded-lg shadow-lg py-1">
                    {TRACK_MAP_THEMES.map((theme) => {
                        const isActive = theme.value === mapStyle;

                        return (
                            <li
                                key={theme.value}
                                className={`flex items-center justify-between gap-x-2 px-3 py-1.5 text-xs cursor-pointer hover:bg-gray50 ${
                                    isActive ? 'text-grayLight900 font-medium' : 'text-grayLight600'
                                }`}
                                onClick={() => {
                                    setMapStyle(theme.value);
                                    setIsOpen(false);
                                }}
                            >
                                <span>{t(`Radar.${theme.labelKey}`)}</span>
                                {isActive && <Check className="w-4 h-4 text-primary flex-none" />}
                            </li>
                        );
                    })}
                </ul>
            )}
        </OutsideClickHandler>
    );
};

export default TrackMapThemePicker;
